// i18n keys — per W2 §3.2 #34 + §7.5.
// Single source of truth for auth-flow error / info strings. Components
// dispatch on `error.code` (per §3.2 #28) and pick the appropriate 繁體中文
// message here. Backend-supplied `error.message` is the fallback for any key
// not yet mapped.

export const AUTH_I18N = {
  // Registration
  register_email_taken: "此電子郵件已被使用",
  register_username_taken: "此使用者名稱已被使用",
  register_duplicate: "此電子郵件或使用者名稱已被使用",
  register_password_too_short: "密碼至少需要 8 個字元",
  register_validation: "註冊資料未通過驗證, 請檢查後重試",
  register_failed: "註冊時發生錯誤, 請稍後再試",

  // Login
  login_invalid_credentials: "帳號或密碼不正確, 請重試",
  // W54.0 C1 — the lockout window is a setting now (`login_lockout_seconds`),
  // so a hard-coded 「15 分鐘」 is only true for the default. This constant is
  // the copy for surfaces that do **not** know the remaining time; where it is
  // known, `routes/Login.tsx` computes the real number from the `Retry-After`
  // header. A fallback that does not state a number cannot be wrong.
  login_account_locked: "帳戶暫時鎖定, 請稍後再試",
  login_validation: "請輸入完整的帳號及密碼",
  login_failed: "登入時發生錯誤, 請稍後再試",

  // Session
  session_required: "請先登入",
  session_expired: "登入狀態已過期, 請重新登入",
  csrf_failed: "CSRF 驗證失敗, 請重新整理頁面",

  // Password
  password_too_short: "密碼至少需要 8 個字元",
  password_reset_sent: "如果帳號存在, 重設連結已寄出",
  password_reset_failed: "重設連結無效或已過期, 請重新申請",
  password_changed: "密碼已更新",

  // Email verification
  email_verification_sent: "驗證信已寄出, 請查收",
  email_verified: "電子郵件已驗證",
  email_verification_failed: "驗證連結無效或已過期, 請重新申請",
  email_not_verified: "請先驗證你的電子郵件",

  // Logout
  logout_success: "已登出",
  logout_failed: "登出時發生錯誤, 請稍後再試",
} as const;

export type AuthI18nKey = keyof typeof AUTH_I18N;

/**
 * The lockout sentence, with the real remaining time when it is known.
 *
 * W54.0 C1 made the lockout window a setting (`login_lockout_seconds`), so a
 * hard-coded 「15 分鐘」 is only true for the default. The truth is the
 * `Retry-After` header, which the api layer reads into
 * `ApiError.retry_after_seconds` (`lib/api/pair.ts` — `apiError`).
 *
 * When the number is not available, the sentence carries **no number at
 * all** rather than guessing one. A fallback that states a duration it does
 * not know is the same defect as an error branch that says 「找不到」 for a
 * request that merely failed.
 */
export function authLockoutMessage(retryAfterSeconds?: number): string {
  if (typeof retryAfterSeconds === "number" && retryAfterSeconds > 0) {
    const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
    return `帳戶暫時鎖定, 請於 ${minutes} 分鐘後重試`;
  }
  return AUTH_I18N.login_account_locked;
}

/**
 * Map an API error code (or generic shape) to its 繁體中文 fallback message.
 * Backend-supplied `error.message` wins when present (server already speaks
 * the user's locale); we only fall back here for codes the server has not
 * customized.
 */
export function authErrorMessage(code: string | undefined, serverMessage?: string): string {
  if (serverMessage && serverMessage.trim()) {
    return serverMessage;
  }
  if (!code) {
    return AUTH_I18N.login_failed;
  }
  switch (code) {
    case "AUTH_EMAIL_TAKEN":
      return AUTH_I18N.register_email_taken;
    case "AUTH_USERNAME_TAKEN":
      return AUTH_I18N.register_username_taken;
    case "AUTH_DUPLICATE":
      return AUTH_I18N.register_duplicate;
    case "AUTH_PASSWORD_TOO_SHORT":
      return AUTH_I18N.password_too_short;
    case "AUTH_INVALID_CREDENTIALS":
      return AUTH_I18N.login_invalid_credentials;
    case "AUTH_ACCOUNT_LOCKED":
      return AUTH_I18N.login_account_locked;
    case "AUTH_SESSION_REQUIRED":
      return AUTH_I18N.session_required;
    case "AUTH_CSRF_FAILED":
      return AUTH_I18N.csrf_failed;
    case "AUTH_INVALID_RESET_TOKEN":
      return AUTH_I18N.password_reset_failed;
    case "AUTH_INVALID_VERIFY_TOKEN":
      return AUTH_I18N.email_verification_failed;
    case "AUTH_EMAIL_NOT_VERIFIED":
      return AUTH_I18N.email_not_verified;
    case "VALIDATION_FAILED":
      return AUTH_I18N.register_validation;
    default:
      return AUTH_I18N.login_failed;
  }
}
